import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { selectAuthUserId } from '../../redux/authSlice';
import {
  getReflectionDatesForMonth,
  fetchReflectionByDate,
} from '../../services/supabaseClient/reflectionService';
import ReflectionEntryForm from './ReflectionEntryForm';
import YearNavigation from '../ui/YearNavigation';
import {
  getTodayDateString,
  formatDateString,
  createDateString,
  getDaysInMonth,
  getFirstDayOfMonth,
  isToday as isTodayUtil,
} from '../../utils/dateUtils';

import './Reflection.css';

const ReflectionCalendar = () => {
  const userId = useSelector(selectAuthUserId);
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth() + 1); // 1-12
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [selectedDate, setSelectedDate] = useState(getTodayDateString());
  const [reflectionDates, setReflectionDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [existingReflection, setExistingReflection] = useState(null);
  const scrollContainerRef = React.useRef(null);

  useEffect(() => {
    const loadData = async () => {
      if (!userId) return;

      setLoading(true);

      // Fetch all reflection dates for the year
      const allDates = [];
      for (let month = 1; month <= 12; month++) {
        const { data: dates } = await getReflectionDatesForMonth(userId, currentYear, month);
        if (dates) {
          allDates.push(...dates);
        }
      }
      setReflectionDates(allDates);

      setLoading(false);
    };

    loadData();
  }, [userId, currentYear]);

  // Load reflection for selected date
  useEffect(() => {
    const loadReflection = async () => {
      if (!userId || !selectedDate) return;

      const { data } = await fetchReflectionByDate(userId, selectedDate);
      setExistingReflection(data);
    };

    loadReflection();
  }, [userId, selectedDate]);

  // Using utility functions from dateUtils

  const handlePreviousYear = () => {
    setCurrentYear(currentYear - 1);
  };

  const handleNextYear = () => {
    setCurrentYear(currentYear + 1);
  };

  const handleToday = () => {
    const now = new Date();
    setCurrentMonth(now.getMonth() + 1);
    setCurrentYear(now.getFullYear());
    setSelectedDate(now.toISOString().split('T')[0]);
  };

  // Scroll to current month on mount and when year/month changes
  useEffect(() => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      // With 2 columns grid, we have 6 rows (12 months / 2 per row)
      // Calculate which row the current month is in (0-indexed)
      const rowIndex = Math.floor((currentMonth - 1) / 2);
      const rowHeight = 200; // Approximate height of one row (2 months) + gap
      const scrollPosition = rowIndex * rowHeight;
      container.scrollTo({
        top: scrollPosition,
        behavior: 'smooth',
      });
    }
  }, [currentMonth, currentYear]);

  const handleDateClick = (year, month, day) => {
    const dateString = createDateString(year, month, day);
    setSelectedDate(dateString);
  };

  const isToday = (year, month, day) => {
    const dateString = createDateString(year, month, day);
    return isTodayUtil(dateString);
  };

  const isSelectedDate = (year, month, day) => {
    const dateString = createDateString(year, month, day);
    return dateString === selectedDate;
  };

  const hasReflection = (year, month, day) => {
    const dateString = createDateString(year, month, day);
    return reflectionDates.includes(dateString);
  };

  const renderMonthCalendar = (month, year) => {
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    const days = [];

    // Add day headers (abbreviated)
    const dayHeaders = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
    dayHeaders.forEach((day, idx) => {
      days.push(
        <div key={`header-${month}-${idx}`} className="mini-calendar-day-header">
          {day}
        </div>
      );
    });

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${month}-${i}`} className="mini-calendar-day empty" />);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const isTodayDate = isToday(year, month, day);
      const hasEntry = hasReflection(year, month, day);
      const isSelected = isSelectedDate(year, month, day);

      days.push(
        <div
          key={`day-${month}-${day}`}
          role="button"
          tabIndex={0}
          className={`mini-calendar-day ${isTodayDate ? 'today' : ''} ${hasEntry ? 'has-entry' : ''} ${isSelected ? 'selected' : ''}`}
          onClick={() => handleDateClick(year, month, day)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleDateClick(year, month, day);
            }
          }}
        >
          <span className="mini-calendar-day-number">{day}</span>
        </div>
      );
    }

    return days;
  };

  const monthNames = [
    'january',
    'february',
    'march',
    'april',
    'may',
    'june',
    'july',
    'august',
    'september',
    'october',
    'november',
    'december',
  ];

  const formatSelectedDate = (dateString) => {
    return formatDateString(dateString);
  };

  if (loading) {
    return (
      <div className="reflection-page-container">
        <div className="reflection-loading">
          <i className="fa-solid fa-spinner fa-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="reflection-page-container">
      {/* Horizontal Scrollable Calendar - Top 1/4 */}
      <div className="reflection-calendar-year">
        <YearNavigation
          year={currentYear}
          onPreviousYear={handlePreviousYear}
          onNextYear={handleNextYear}
          onToday={handleToday}
        />

        <div className="horizontal-calendar-container" ref={scrollContainerRef}>
          <div className="horizontal-calendar-track">
            {monthNames.map((monthName, idx) => {
              const month = idx + 1;
              const isCurrentMonth = month === currentMonth;
              return (
                <div
                  key={`${currentYear}-${month}`}
                  className={`month-calendar-horizontal ${isCurrentMonth ? 'center-month' : ''}`}
                >
                  <div className="month-name">{monthName}</div>
                  <div className="month-grid">{renderMonthCalendar(month, currentYear)}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Reflection Content - Bottom 3/4 */}
      <div className="reflection-content-area">
        <div className="reflection-content-header">
          <h2>{formatSelectedDate(selectedDate)}</h2>
        </div>
        {existingReflection ? (
          // Show existing reflection for any date with reflection
          <div className="reflection-view">
            <div className="reflection-view-section">
              <h3>Your Responses</h3>
              {existingReflection.user_responses && (
                <div className="user-responses">
                  {existingReflection.user_responses.what_learned && (
                    <div className="response-item">
                      <strong>What I learned:</strong>
                      <p>{existingReflection.user_responses.what_learned}</p>
                    </div>
                  )}
                  {existingReflection.user_responses.most_challenging && (
                    <div className="response-item">
                      <strong>Most challenging:</strong>
                      <p>{existingReflection.user_responses.most_challenging}</p>
                    </div>
                  )}
                  {existingReflection.user_responses.aha_moment && (
                    <div className="response-item">
                      <strong>Aha moment:</strong>
                      <p>{existingReflection.user_responses.aha_moment}</p>
                    </div>
                  )}
                  {existingReflection.user_responses.understanding_rating && (
                    <div className="response-item">
                      <strong>Understanding:</strong>
                      <p>{existingReflection.user_responses.understanding_rating}/5</p>
                    </div>
                  )}
                  {existingReflection.user_responses.engagement_rating && (
                    <div className="response-item">
                      <strong>Engagement:</strong>
                      <p>{existingReflection.user_responses.engagement_rating}/5</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="reflection-view-section">
              <h3>Reflection Entry</h3>
              <div className="reflection-entry-text">{existingReflection.reflection_entry}</div>
            </div>
          </div>
        ) : (
          // No reflection for this date - allow creating one for any past/present day
          <ReflectionEntryForm date={selectedDate} compact />
        )}
      </div>
    </div>
  );
};

export default ReflectionCalendar;
